import { PERMISSION_SNAPSHOT_KEY_PREFIX } from '@streamlain/permission-snapshot-contract';

import { uint8ArrayToArrayBuffer } from '../../utils/typed-array';

const VAULT_DB_NAME = 'streamlain-local-authority-vault';
const VAULT_DB_VERSION = 1;
const KEY_STORE = 'keys';
const ENTRY_STORE = 'entries';
const DEFAULT_KEY_ID = 'local-authority:v1';
const CLIENT_INSTALL_ID_KEY = 'streamlain-local-authority-client-install-id';

export type LocalAuthorityVaultStatus =
  | 'ready'
  | 'unavailable'
  | 'locked'
  | 'corrupt'
  | 'unsupported'
  | 'degraded';

export interface LocalAuthorityVaultRead<T> {
  status: LocalAuthorityVaultStatus;
  value: T | null;
}

interface VaultEntry {
  iv: string;
  data: string;
  updatedAt: number;
}

interface ElectronLocalAuthorityVault {
  readFeatureEntitlement<T>(key: string): Promise<LocalAuthorityVaultRead<T>>;
  writeFeatureEntitlement<T>(
    key: string,
    value: T
  ): Promise<LocalAuthorityVaultStatus>;
  deleteFeatureEntitlement(key: string): Promise<LocalAuthorityVaultStatus>;
  readPermissionSnapshot<T>(key: string): Promise<LocalAuthorityVaultRead<T>>;
  writePermissionSnapshot<T>(
    key: string,
    value: T
  ): Promise<LocalAuthorityVaultStatus>;
  deletePermissionSnapshot(key: string): Promise<LocalAuthorityVaultStatus>;
}

type LocalAuthorityVaultPurpose =
  | 'feature-entitlement'
  | 'workspace-permission-snapshot';

export type LocalAuthorityKeyProviderPlatform = 'web' | 'electron' | 'mobile';

export interface LocalAuthorityKeyProviderPort {
  readonly platform: LocalAuthorityKeyProviderPlatform;
  isSupported(): boolean;
  readFeatureEntitlement<T>(key: string): Promise<LocalAuthorityVaultRead<T>>;
  writeFeatureEntitlement<T>(
    key: string,
    value: T
  ): Promise<LocalAuthorityVaultStatus>;
  deleteFeatureEntitlement(key: string): Promise<LocalAuthorityVaultStatus>;
  readPermissionSnapshot<T>(key: string): Promise<LocalAuthorityVaultRead<T>>;
  writePermissionSnapshot<T>(
    key: string,
    value: T
  ): Promise<LocalAuthorityVaultStatus>;
  deletePermissionSnapshot(key: string): Promise<LocalAuthorityVaultStatus>;
}

export interface MobileLocalAuthorityKeyProviderPort extends LocalAuthorityKeyProviderPort {
  readonly platform: 'mobile';
}

let dbPromise: Promise<IDBDatabase> | null = null;
let keyPromise: Promise<CryptoKey> | null = null;
let webKeyProvider: WebLocalAuthorityKeyProvider | null = null;
let mobileKeyProvider: MobileLocalAuthorityKeyProviderPort | null = null;

export function registerMobileLocalAuthorityKeyProvider(
  provider: MobileLocalAuthorityKeyProviderPort
): () => void {
  if (provider.platform !== 'mobile') {
    throw new Error('LocalAuthority mobile key provider has invalid platform');
  }
  mobileKeyProvider = provider;
  return () => {
    if (mobileKeyProvider === provider) {
      mobileKeyProvider = null;
    }
  };
}

export function readFeatureEntitlementVault<T>(
  key: string
): Promise<LocalAuthorityVaultRead<T>> {
  return readPurposeBoundVault<T>(key, 'feature-entitlement');
}

export function writeFeatureEntitlementVault<T>(
  key: string,
  value: T
): Promise<LocalAuthorityVaultStatus> {
  return writePurposeBoundVault(key, 'feature-entitlement', value);
}

export function deleteFeatureEntitlementVault(
  key: string
): Promise<LocalAuthorityVaultStatus> {
  return deletePurposeBoundVault(key, 'feature-entitlement');
}

export function readPermissionSnapshotVault<T>(
  key: string
): Promise<LocalAuthorityVaultRead<T>> {
  return readPurposeBoundVault<T>(key, 'workspace-permission-snapshot');
}

export function writePermissionSnapshotVault<T>(
  key: string,
  value: T
): Promise<LocalAuthorityVaultStatus> {
  return writePurposeBoundVault(key, 'workspace-permission-snapshot', value);
}

export function deletePermissionSnapshotVault(
  key: string
): Promise<LocalAuthorityVaultStatus> {
  return deletePurposeBoundVault(key, 'workspace-permission-snapshot');
}

/** App release confirmations live in the permission vault under their own
 * key prefix, so every key provider (web, Electron, mobile) holds them. */
export function readAppReleaseVault<T>(
  key: string
): Promise<LocalAuthorityVaultRead<T>> {
  return readPermissionSnapshotVault<T>(appReleaseVaultKey(key));
}

export function writeAppReleaseVault<T>(
  key: string,
  value: T
): Promise<LocalAuthorityVaultStatus> {
  return writePermissionSnapshotVault(appReleaseVaultKey(key), value);
}

export function deleteAppReleaseVault(
  key: string
): Promise<LocalAuthorityVaultStatus> {
  return deletePermissionSnapshotVault(appReleaseVaultKey(key));
}

function appReleaseVaultKey(key: string): string {
  if (!key.startsWith(PERMISSION_SNAPSHOT_KEY_PREFIX.appRelease)) {
    throw new Error('App release vault key must carry the App release prefix');
  }
  return key;
}

export function isVaultSupported(): boolean {
  return getLocalAuthorityKeyProvider() !== null;
}

export function getLocalAuthorityClientInstallId(): string {
  const storage = getLocalStorage();
  if (!storage) {
    return 'web-storage-unavailable';
  }
  const existing = storage.getItem(CLIENT_INSTALL_ID_KEY);
  if (existing) {
    return existing;
  }
  const id =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `install-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  try {
    storage.setItem(CLIENT_INSTALL_ID_KEY, id);
    return id;
  } catch {
    return 'web-storage-locked';
  }
}

export function stableLocalAuthorityScopeHash(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let hash = 0x811c9dc5;
  for (const byte of bytes) {
    hash ^= byte;
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

async function getOrCreateVaultKey(db: IDBDatabase): Promise<CryptoKey> {
  if (!keyPromise) {
    keyPromise = (async () => {
      const stored = await idbGet<CryptoKey>(db, KEY_STORE, DEFAULT_KEY_ID);
      if (stored) {
        return stored;
      }
      const key = await crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
      );
      await idbPut(db, KEY_STORE, DEFAULT_KEY_ID, key);
      return key;
    })();
  }
  return keyPromise;
}

function getElectronVault(): ElectronLocalAuthorityVault | null {
  const apis = (
    globalThis as unknown as {
      __apis?: {
        localAuthorityVault?: ElectronLocalAuthorityVault;
      };
    }
  ).__apis;
  return apis?.localAuthorityVault ?? null;
}

function getLocalAuthorityKeyProvider(): LocalAuthorityKeyProviderPort | null {
  const electronVault = getElectronVault();
  if (electronVault) {
    return new ElectronLocalAuthorityKeyProvider(electronVault);
  }
  if (mobileKeyProvider?.isSupported()) {
    return mobileKeyProvider;
  }
  const webProvider = getWebLocalAuthorityKeyProvider();
  return webProvider.isSupported() ? webProvider : null;
}

function getWebLocalAuthorityKeyProvider(): WebLocalAuthorityKeyProvider {
  webKeyProvider ??= new WebLocalAuthorityKeyProvider();
  return webKeyProvider;
}

async function readPurposeBoundVault<T>(
  key: string,
  purpose: LocalAuthorityVaultPurpose
): Promise<LocalAuthorityVaultRead<T>> {
  if (!isPurposeAllowedForKey(key, purpose)) {
    return { status: 'unsupported', value: null };
  }
  const provider = getLocalAuthorityKeyProvider();
  if (!provider) {
    return { status: 'unsupported', value: null };
  }
  return purpose === 'feature-entitlement'
    ? provider.readFeatureEntitlement<T>(key)
    : provider.readPermissionSnapshot<T>(key);
}

async function writePurposeBoundVault<T>(
  key: string,
  purpose: LocalAuthorityVaultPurpose,
  value: T
): Promise<LocalAuthorityVaultStatus> {
  if (!isPurposeAllowedForKey(key, purpose)) {
    return 'unsupported';
  }
  const provider = getLocalAuthorityKeyProvider();
  if (!provider) {
    return 'unsupported';
  }
  return purpose === 'feature-entitlement'
    ? provider.writeFeatureEntitlement(key, value)
    : provider.writePermissionSnapshot(key, value);
}

async function deletePurposeBoundVault(
  key: string,
  purpose: LocalAuthorityVaultPurpose
): Promise<LocalAuthorityVaultStatus> {
  if (!isPurposeAllowedForKey(key, purpose)) {
    return 'unsupported';
  }
  const provider = getLocalAuthorityKeyProvider();
  if (!provider) {
    return 'unsupported';
  }
  return purpose === 'feature-entitlement'
    ? provider.deleteFeatureEntitlement(key)
    : provider.deletePermissionSnapshot(key);
}

class ElectronLocalAuthorityKeyProvider implements LocalAuthorityKeyProviderPort {
  readonly platform = 'electron' as const;

  constructor(private readonly vault: ElectronLocalAuthorityVault) {}

  isSupported(): boolean {
    return true;
  }

  readFeatureEntitlement<T>(key: string): Promise<LocalAuthorityVaultRead<T>> {
    return this.vault.readFeatureEntitlement<T>(key);
  }

  writeFeatureEntitlement<T>(
    key: string,
    value: T
  ): Promise<LocalAuthorityVaultStatus> {
    return this.vault.writeFeatureEntitlement(key, value);
  }

  deleteFeatureEntitlement(key: string): Promise<LocalAuthorityVaultStatus> {
    return this.vault.deleteFeatureEntitlement(key);
  }

  readPermissionSnapshot<T>(key: string): Promise<LocalAuthorityVaultRead<T>> {
    return this.vault.readPermissionSnapshot<T>(key);
  }

  writePermissionSnapshot<T>(
    key: string,
    value: T
  ): Promise<LocalAuthorityVaultStatus> {
    return this.vault.writePermissionSnapshot(key, value);
  }

  deletePermissionSnapshot(key: string): Promise<LocalAuthorityVaultStatus> {
    return this.vault.deletePermissionSnapshot(key);
  }
}

class WebLocalAuthorityKeyProvider implements LocalAuthorityKeyProviderPort {
  readonly platform = 'web' as const;

  isSupported(): boolean {
    return (
      typeof globalThis.crypto !== 'undefined' &&
      Boolean(globalThis.crypto.subtle) &&
      typeof globalThis.crypto.getRandomValues === 'function' &&
      typeof globalThis.indexedDB !== 'undefined'
    );
  }

  readFeatureEntitlement<T>(key: string): Promise<LocalAuthorityVaultRead<T>> {
    return this.readVault<T>(key, 'feature-entitlement');
  }

  writeFeatureEntitlement<T>(
    key: string,
    value: T
  ): Promise<LocalAuthorityVaultStatus> {
    return this.writeVault(key, 'feature-entitlement', value);
  }

  deleteFeatureEntitlement(key: string): Promise<LocalAuthorityVaultStatus> {
    return this.deleteVault(key, 'feature-entitlement');
  }

  readPermissionSnapshot<T>(key: string): Promise<LocalAuthorityVaultRead<T>> {
    return this.readVault<T>(key, 'workspace-permission-snapshot');
  }

  writePermissionSnapshot<T>(
    key: string,
    value: T
  ): Promise<LocalAuthorityVaultStatus> {
    return this.writeVault(key, 'workspace-permission-snapshot', value);
  }

  deletePermissionSnapshot(key: string): Promise<LocalAuthorityVaultStatus> {
    return this.deleteVault(key, 'workspace-permission-snapshot');
  }

  private async readVault<T>(
    key: string,
    purpose: LocalAuthorityVaultPurpose
  ): Promise<LocalAuthorityVaultRead<T>> {
    if (!isPurposeAllowedForKey(key, purpose)) {
      return { status: 'unsupported', value: null };
    }
    if (!this.isSupported()) {
      return { status: 'unsupported', value: null };
    }
    try {
      const db = await openVaultDb();
      const entry = await idbGet<VaultEntry>(db, ENTRY_STORE, key);
      if (!entry) {
        return { status: 'ready', value: null };
      }
      const cryptoKey = await getOrCreateVaultKey(db);
      const plaintext = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: uint8ArrayToArrayBuffer(base64ToBytes(entry.iv)),
          additionalData: uint8ArrayToArrayBuffer(aadForKey(key)),
        },
        cryptoKey,
        uint8ArrayToArrayBuffer(base64ToBytes(entry.data))
      );
      return {
        status: 'ready',
        value: JSON.parse(new TextDecoder().decode(plaintext)) as T,
      };
    } catch {
      return { status: 'corrupt', value: null };
    }
  }

  private async writeVault<T>(
    key: string,
    purpose: LocalAuthorityVaultPurpose,
    value: T
  ): Promise<LocalAuthorityVaultStatus> {
    if (!isPurposeAllowedForKey(key, purpose)) {
      return 'unsupported';
    }
    if (!this.isSupported()) {
      return 'unsupported';
    }
    try {
      const db = await openVaultDb();
      const cryptoKey = await getOrCreateVaultKey(db);
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const data = await crypto.subtle.encrypt(
        {
          name: 'AES-GCM',
          iv: uint8ArrayToArrayBuffer(iv),
          additionalData: uint8ArrayToArrayBuffer(aadForKey(key)),
        },
        cryptoKey,
        uint8ArrayToArrayBuffer(new TextEncoder().encode(JSON.stringify(value)))
      );
      await idbPut<VaultEntry>(db, ENTRY_STORE, key, {
        iv: bytesToBase64(iv),
        data: bytesToBase64(new Uint8Array(data)),
        updatedAt: Date.now(),
      });
      return 'ready';
    } catch {
      return 'degraded';
    }
  }

  private async deleteVault(
    key: string,
    purpose: LocalAuthorityVaultPurpose
  ): Promise<LocalAuthorityVaultStatus> {
    if (!isPurposeAllowedForKey(key, purpose)) {
      return 'unsupported';
    }
    if (!this.isSupported()) {
      return 'unsupported';
    }
    try {
      const db = await openVaultDb();
      await idbDelete(db, ENTRY_STORE, key);
      return 'ready';
    } catch {
      return 'degraded';
    }
  }
}

function getLocalStorage(): Storage | null {
  try {
    return typeof globalThis.localStorage === 'undefined'
      ? null
      : globalThis.localStorage;
  } catch {
    return null;
  }
}

function isPurposeAllowedForKey(
  key: string,
  purpose: LocalAuthorityVaultPurpose
): boolean {
  if (purpose === 'feature-entitlement') {
    return key.startsWith('sl-features-v3-signed-entitlements-');
  }
  if (purpose === 'workspace-permission-snapshot') {
    return Object.values(PERMISSION_SNAPSHOT_KEY_PREFIX).some(prefix =>
      key.startsWith(prefix)
    );
  }
  return false;
}

function aadForKey(key: string): Uint8Array {
  return new TextEncoder().encode(`streamlain-local-authority-vault:v1:${key}`);
}

function openVaultDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(VAULT_DB_NAME, VAULT_DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(KEY_STORE)) {
          db.createObjectStore(KEY_STORE);
        }
        if (!db.objectStoreNames.contains(ENTRY_STORE)) {
          db.createObjectStore(ENTRY_STORE);
        }
      };
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  }
  return dbPromise;
}

function idbGet<T>(
  db: IDBDatabase,
  storeName: string,
  key: string
): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const request = tx.objectStore(storeName).get(key);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result as T | undefined);
  });
}

function idbPut<T>(
  db: IDBDatabase,
  storeName: string,
  key: string,
  value: T
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const request = tx.objectStore(storeName).put(value, key);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function idbDelete(
  db: IDBDatabase,
  storeName: string,
  key: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const request = tx.objectStore(storeName).delete(key);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
